import clsx from 'clsx';
import { useRef, useState, type DragEvent } from 'react';

import styles from './Upload.module.css';

export interface DropzoneProps {
  readonly onFiles: (files: readonly File[]) => void;
  readonly accept?: string;
  readonly disabled?: boolean;
  readonly label?: string;
  readonly hint?: string;
}

/** Click-or-drop file input. Owns no upload logic — it only emits files. */
export function Dropzone({
  onFiles,
  accept = '.pdf,.docx,.pptx,.jpg,.jpeg,.png',
  disabled,
  label = 'Click to upload, or drop files here',
  hint = 'PDF · DOCX · PPTX · JPG · PNG',
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const emit = (list: FileList | null) => {
    // Guarded here as well as on the controls: the hidden input is reachable
    // without going through the button, so a read-only role must be refused at
    // the point files are emitted, not only where they are picked.
    if (disabled || !list || list.length === 0) return;
    onFiles([...list]);
  };

  const onDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) emit(event.dataTransfer.files);
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        className={clsx(styles.dropzone, dragging && styles.dragging)}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={onDrop}
      >
        <span aria-hidden="true">📎</span>
        <span>{label}</span>
        <span className={styles.hint}>{hint}</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        disabled={disabled}
        className="visually-hidden"
        tabIndex={-1}
        onChange={(e) => {
          emit(e.target.files);
          e.target.value = '';
        }}
      />
    </>
  );
}
